import React, { useEffect, useState } from "react";
import { Table, Spinner, Badge } from "react-bootstrap";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { WEEK_META, weekTitle, shortDate } from "../utils/guidanceWeeks";

const API = process.env.REACT_APP_API;

const HEALTH_FIELDS = [
  { key: "has_everSyndrome", label: "โรคที่เคยเป็น" },
  { key: "has_drug", label: "แพ้ยา" },
  { key: "has_food", label: "แพ้อาหาร" },
  { key: "has_syndrome", label: "โรคประจำตัว" },
  { key: "has_facingSyndrome", label: "วิธีแก้ไข" },
  { key: "has_accidentHistory", label: "อุบัติเหตุ/อื่นๆ" },
];

const INFO_FIELDS = [
  { key: "has_idcard", label: "เลขบัตรประชาชน" },
  { key: "has_birthdate", label: "วันเกิด" },
  { key: "has_gender", label: "เพศ" },
  { key: "has_address", label: "ที่อยู่ปัจจุบัน" },
  { key: "has_contact", label: "ผู้ปกครองติดต่อได้" },
];

// บัญชีรายสัปดาห์ของคาบแนะแนว — ตอบว่าสัปดาห์ที่หายไปเป็นวันหยุด/กิจกรรมตามปฏิทิน
// ปฏิบัติงาน หรือครูประจำชั้นยังไม่ได้เช็คจริง (ข้อมูลมากับแถวห้อง ไม่ต้องยิง API เพิ่ม)
const GuidanceWeekSummary = ({ weeks }) => {
  if (!weeks || weeks.length === 0) return null;
  const noTeach = weeks.filter((w) => w.status === "no_teaching");
  const missed = weeks.filter((w) => w.status === "missed");
  return (
    <div className="mb-3">
      <div className="d-flex flex-wrap gap-1 mb-2">
        {weeks.map((w) => (
          <Badge
            key={w.date}
            bg={WEEK_META[w.status].bg}
            className={`fs-xs${w.status === "upcoming" ? " text-dark border" : ""}`}
            title={weekTitle(w)}
          >
            {shortDate(w.date)}
          </Badge>
        ))}
      </div>
      {noTeach.length > 0 && (
        <div className="fs-xs text-muted">
          <i className="ri-calendar-close-line me-1"></i>
          งดตามปฏิทิน (ไม่นับเป็นตัวหาร):{" "}
          {noTeach.map((w) => `${shortDate(w.date)} ${w.reason}`).join(" · ")}
        </div>
      )}
      {missed.length > 0 && (
        <div className="fs-xs text-danger">
          <i className="ri-error-warning-line me-1"></i>
          ยังไม่เช็ค (โรงเรียนเปิดตามปกติ): {missed.map((w) => shortDate(w.date)).join(" · ")}
        </div>
      )}
    </div>
  );
};

// แนะแนว — รายครั้งที่เช็ค (หัวข้อ + ผลการเข้าร่วม)
const GuidanceSessions = ({ rows }) => (
  <Table size="sm" hover responsive className="mb-0 align-middle">
    <thead className="table-secondary">
      <tr>
        <th style={{ width: 110 }}>วันที่</th>
        <th>หัวข้อ</th>
        <th>ผู้เช็ค</th>
        <th className="text-center" style={{ width: 60 }}>มา</th>
        <th className="text-center" style={{ width: 60 }}>ขาด</th>
        <th className="text-center" style={{ width: 60 }}>สาย</th>
        <th className="text-center" style={{ width: 60 }}>ลา</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.gtr_id}>
          <td>{r.att_date}</td>
          <td className="fs-sm">{r.gtr_topic}</td>
          <td className="fs-sm">{r.checked_by_name || <span className="text-muted">-</span>}</td>
          <td className="text-center">{Number(r.presents || 0)}</td>
          <td className="text-center">{Number(r.absents || 0)}</td>
          <td className="text-center">{Number(r.lates || 0)}</td>
          <td className="text-center">{Number(r.leaves || 0)}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

const YesNo = ({ v }) =>
  v ? (
    <i className="ri-checkbox-circle-fill text-success"></i>
  ) : (
    <i className="ri-close-circle-line text-danger"></i>
  );

// อ่านอย่างเดียว — โหลดรายละเอียดของงานหนึ่งเมื่อ expand แถวห้อง
export default function AdvisorTaskDetail({ duty, rId, yearId, semester, month, weeks }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (semester) params.set("semester", semester);
    if (month) params.set("month", month);
    axiosInstance
      .get(`${API}/getAdvisorTaskDetail/${duty}/${rId}/${yearId}?${params.toString()}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      })
      .then((res) => { if (active) setRows(res.data || []); })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [duty, rId, yearId, semester, month]);

  const spinner = (
    <div className="text-center text-muted py-3">
      <Spinner animation="border" size="sm" /> กำลังโหลด...
    </div>
  );

  // แนะแนว — บัญชีรายสัปดาห์มาก่อนเสมอ เพราะห้องที่ยังไม่เคยเช็คเลยคือกรณีที่
  // ต้องรู้เหตุผลมากที่สุด (early return ด้านล่างจะซ่อนมันไป)
  if (duty === "guidance") {
    return (
      <>
        <GuidanceWeekSummary weeks={weeks} />
        {loading ? spinner : rows.length === 0 ? (
          <div className="text-center text-muted py-3">
            <i className="ri-inbox-line fs-4 d-block mb-1"></i>
            ยังไม่มีการเช็คชื่อคาบแนะแนว
          </div>
        ) : (
          <GuidanceSessions rows={rows} />
        )}
      </>
    );
  }

  if (loading) return spinner;

  if (rows.length === 0) {
    return (
      <div className="text-center text-muted py-3">
        <i className="ri-inbox-line fs-4 d-block mb-1"></i>
        ไม่มีข้อมูล
      </div>
    );
  }

  // น้ำหนัก/ส่วนสูง — รายคน
  if (duty === "bodymass") {
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 90 }}>เลขประจำตัว</th>
            <th>ชื่อ-สกุล</th>
            <th className="text-center" style={{ width: 100 }}>ส่วนสูง (ซม.)</th>
            <th className="text-center" style={{ width: 100 }}>น้ำหนัก (กก.)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const done = r.stbm_height != null || r.stbm_weight != null;
            return (
              <tr key={i} className={done ? "" : "text-muted"}>
                <td>{r.st_idnumber}</td>
                <td className="fs-sm">{r.st_name}</td>
                <td className="text-center">{r.stbm_height != null ? r.stbm_height : "—"}</td>
                <td className="text-center">{r.stbm_weight != null ? r.stbm_weight : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  // คุณลักษณะอันพึงประสงค์ — รายคน
  if (duty === "desirable") {
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 90 }}>เลขประจำตัว</th>
            <th>ชื่อ-สกุล</th>
            <th className="text-center" style={{ width: 120 }}>กรอกแล้ว (ข้อ)</th>
            <th className="text-center" style={{ width: 100 }}>สถานะ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const filled = Number(r.filled_items || 0);
            return (
              <tr key={i}>
                <td>{r.st_idnumber}</td>
                <td className="fs-sm">{r.st_name}</td>
                <td className="text-center">{filled}/8</td>
                <td className="text-center">
                  {filled >= 8 ? (
                    <Badge bg="success">ครบ</Badge>
                  ) : filled > 0 ? (
                    <Badge bg="warning">ไม่ครบ</Badge>
                  ) : (
                    <Badge bg="danger">ยังไม่ทำ</Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  // ความเห็นครูที่ปรึกษา — รายคน
  if (duty === "opinion") {
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 90 }}>เลขประจำตัว</th>
            <th>ชื่อ-สกุล</th>
            <th className="text-center" style={{ width: 120 }}>บันทึกเมื่อ</th>
            <th className="text-center" style={{ width: 100 }}>สถานะ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.st_idnumber}</td>
              <td className="fs-sm">{r.st_name}</td>
              <td className="text-center">{r.updated_at || <span className="text-muted">—</span>}</td>
              <td className="text-center">
                {Number(r.recorded) ? <Badge bg="success">บันทึกแล้ว</Badge> : <Badge bg="danger">ยังไม่ทำ</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  // สุขภาพ / ข้อมูลนักเรียน — ตารางฟิลด์ ✓/✗ (ไม่แสดงค่าจริง)
  const fields = duty === "health" ? HEALTH_FIELDS : INFO_FIELDS;
  return (
    <Table size="sm" hover responsive className="mb-0 align-middle">
      <thead className="table-secondary">
        <tr>
          <th style={{ width: 90 }}>เลขประจำตัว</th>
          <th>ชื่อ-สกุล</th>
          {fields.map((f) => (
            <th key={f.key} className="text-center">{f.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{r.st_idnumber}</td>
            <td className="fs-sm">{r.st_name}</td>
            {fields.map((f) => (
              <td key={f.key} className="text-center"><YesNo v={Number(r[f.key])} /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
